import {CommonModule} from '@angular/common';
import {Component, Input, OnChanges, OnInit, SimpleChanges, inject} from '@angular/core';
import {FormBuilder, ReactiveFormsModule, Validators} from '@angular/forms';
import {finalize} from 'rxjs';
import {MaterialModule} from '../../material.module';
import {IProductFeedOptions} from '../../models/product-feed-options.model';
import {ProductQueryTestResponse, ProductQueryTestResult, ProductQueryTestService} from '../../services/ai/product-query-test.service';
import {ProductFeedService} from '../../services/objects/product-feed.service';

@Component({
  selector: 'app-product-query-test',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MaterialModule],
  templateUrl: './product-query-test.component.html',
  styleUrls: ['./product-query-test.component.scss']
})
export class ProductQueryTestComponent implements OnInit, OnChanges {
  private readonly fb = inject(FormBuilder);
  private readonly queryTestService = inject(ProductQueryTestService);
  private readonly productFeedService = inject(ProductFeedService);

  readonly displayedColumns = ['rank', 'product', 'constraints', 'feed', 'score', 'actions'];

  @Input() productFeed: IProductFeedOptions | null = null;
  @Input() embedded = false;

  form = this.fb.group({
    query: ['terms:*ull* AND size:m', [Validators.required]],
    categories: [''],
    productFeedIds: [[] as string[]],
    market: [''],
    limit: [10, [Validators.min(1), Validators.max(50)]]
  });

  productFeeds: IProductFeedOptions[] = [];
  loading = false;
  loadingFeeds = false;
  errorMessage = '';
  response: ProductQueryTestResponse | null = null;
  expandedRawByRank: Record<number, boolean> = {};

  ngOnInit(): void {
    this.applyProductFeedContext();
    if (!this.embedded) {
      this.loadProductFeeds();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['productFeed'] || changes['embedded']) {
      this.applyProductFeedContext();
    }
  }

  runQuery(): void {
    if (this.form.invalid || this.loading) {
      this.form.markAllAsTouched();
      return;
    }

    const value = this.form.getRawValue();
    this.loading = true;
    this.errorMessage = '';
    this.response = null;
    this.expandedRawByRank = {};

    this.queryTestService.testQuery({
      query: value.query?.trim() ?? '',
      categories: this.splitCsv(value.categories),
      productFeedIds: value.productFeedIds ?? [],
      market: value.market?.trim() ?? '',
      limit: Number(value.limit || 10)
    }).pipe(
      finalize(() => this.loading = false)
    ).subscribe({
      next: response => this.response = response,
      error: error => {
        this.errorMessage = error?.error || error?.message || 'Query test failed.';
      }
    });
  }

  private applyProductFeedContext(): void {
    if (!this.embedded || !this.productFeed) {
      return;
    }

    const rootFeedId = String(this.productFeed.sourceFeedId ?? '').trim() || this.productFeed.id;
    this.productFeeds = this.productFeed.sourceFeedId ? [] : [this.productFeed];
    this.form.patchValue({
      productFeedIds: rootFeedId ? [rootFeedId] : [],
      market: this.productFeed.market ?? ''
    }, {emitEvent: false});
  }

  get queryScopeLabel(): string {
    if (!this.productFeed) return 'the product index';
    return String(this.productFeed.sourceFeedId ?? '').trim()
      ? `root feed ${this.productFeed.sourceFeedId}`
      : this.productFeed.name || this.productFeed.id;
  }

  clear(): void {
    this.form.patchValue({
      query: '',
      categories: '',
      productFeedIds: [],
      market: '',
      limit: 10
    });
    this.response = null;
    this.errorMessage = '';
    this.expandedRawByRank = {};
  }

  trackResult(_: number, result: ProductQueryTestResult): number {
    return result.rank;
  }

  toggleRaw(result: ProductQueryTestResult): void {
    this.expandedRawByRank[result.rank] = !this.expandedRawByRank[result.rank];
  }

  rawJson(result: ProductQueryTestResult): string {
    return JSON.stringify(result.raw ?? {}, null, 2);
  }

  productLabel(result: ProductQueryTestResult): string {
    return result.name || result.title || result.sku || 'Untitled product';
  }

  private loadProductFeeds(): void {
    this.loadingFeeds = true;
    this.productFeedService.getAllAsync()
      .pipe(finalize(() => this.loadingFeeds = false))
      .subscribe({
        next: feeds => this.productFeeds = (feeds ?? []).filter(feed => !String(feed.sourceFeedId ?? '').trim()),
        error: () => this.productFeeds = []
      });
  }

  private splitCsv(value: string | null | undefined): string[] {
    return `${value ?? ''}`
      .split(',')
      .map(item => item.trim())
      .filter(Boolean);
  }
}
